import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";

export interface HackerProfile {
  id: number;
  profile_id: string | null;
  name: string;
  specialty: string;
  avatar: string | null;
  rating: number | null;
  rate: string | null;
  experience: string | null;
  age: number | null;
  gender: "male" | "female" | null;
  skills: string[] | null;
  country: string | null;
  prizes: string[] | null;
  certifications: string[] | null;
  availability: string | null;
  verified: boolean | null;
  featured: boolean | null;
}

export function useHackers() {
  const [hackers, setHackers] = useState<HackerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHackers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabase();
      const { data, error: supaError } = await supabase
        .from("hacker_listings")
        .select("*")
        .order("rating", { ascending: false });

      if (supaError) throw supaError;
      setHackers(data as HackerProfile[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load hackers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHackers();
  }, [fetchHackers]);

  return { hackers, loading, error, refetch: fetchHackers };
}

export function useHacker(id: number | null) {
  const [hacker, setHacker] = useState<HackerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHacker = useCallback(async (hackerId: number) => {
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabase();
      const { data, error: supaError } = await supabase
        .from("hacker_listings")
        .select("*")
        .eq("id", hackerId)
        .maybeSingle();

      if (supaError) throw supaError;
      setHacker(data as HackerProfile | null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load hacker");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id !== null && id !== undefined && !isNaN(id)) {
      fetchHacker(id);
    } else {
      setLoading(false);
    }
  }, [id, fetchHacker]);

  return { hacker, loading, error, refetch: () => id && fetchHacker(id) };
}